// snippetDropPlugin.js
// Plugin for handling drag-and-drop of snippets into the editor
// Creates or updates reference marks with evidence

import {Plugin, PluginKey, TextSelection} from 'prosemirror-state';
import {addMetaMark} from '../helpers/metaMarksHelpers';
import {addReference, initializeReferenceMark} from '../yMaps/referencesYMap';
import {syncBlockClassificationFloorForReference} from '../helpers/referenceClassificationFloor';
import useYDocStore from '../../../stores/useYDocStore';
import {isProductReportSource} from '../../segmentItem/sourceCitePolicy';

export const snippetDropPluginKey = new PluginKey('snippetDrop');
const SNIPPET_DRAG_MIME = 'application/x-snippet';
const SNIPPETS_DRAG_MIME = 'application/x-snippets';

/**
 * Helper: compute a stable localId for a snippet (align with sourcePackageSlice logic)
 */
const computeLocalIdFromSnippet = (snippet) => {
    if (!snippet) return null;
    if (snippet.localId) return snippet.localId;
    const s = snippet.start || {};
    const e = snippet.end || {};
    const sIdx = (s.segmentIndex != null ? s.segmentIndex : snippet.index);
    const sOff = (s.segmentOffset != null ? s.segmentOffset : (snippet.offset ?? 0));
    const eIdx = (e.segmentIndex != null ? e.segmentIndex : sIdx);
    const eOff = (e.segmentOffset != null ? e.segmentOffset : sOff);
    if (snippet.docId != null && sIdx != null && sOff != null && eIdx != null && eOff != null) {
        return [snippet.docId, sIdx, sOff, eIdx, eOff].join(':');
    }
    return null;
};

/**
 * Resolve a reference mark UID from a ProseMirror document position.
 * This is more reliable than DOM walking because decorations/widgets don't
 * carry the mark's DOM attributes.
 */
const referenceUidFromDocPos = (state, pos) => {
    const refType = state.schema.marks.reference;
    if (!refType) return null;

    const safePos = Math.max(0, Math.min(pos, state.doc.content.size));
    const $pos = state.doc.resolve(safePos);

    // Prefer marks at the position.
    const direct = $pos.marks().find(m => m.type === refType);
    if (direct) return direct.attrs.uid || null;

    // Also check the node before/after for marks (useful when pos is at a boundary).
    const before = state.doc.nodeAt(Math.max(0, safePos - 1));
    if (before?.isText) {
        const m = before.marks.find(mm => mm.type === refType);
        if (m) return m.attrs.uid || null;
    }

    const after = state.doc.nodeAt(safePos);
    if (after?.isText) {
        const m = after.marks.find(mm => mm.type === refType);
        if (m) return m.attrs.uid || null;
    }

    return null;
};

const DEBUG_SNIPPET_DROP =
    typeof window !== 'undefined' &&
    (window.__DEBUG_SNIPPET_DROP__ || localStorage.getItem('DEBUG_SNIPPET_DROP') === '1');

const isWordCharacter = (char) => typeof char === 'string' && char.length > 0 && !/\s/.test(char);

const getTextCharBefore = (doc, pos) => {
    if (!doc || pos <= 0) return null;
    const $pos = doc.resolve(pos);
    const before = $pos.nodeBefore;
    if (!before?.isText || !before.text) return null;
    return before.text[before.text.length - 1] || null;
};

const getTextCharAfter = (doc, pos) => {
    if (!doc || pos >= doc.content.size) return null;
    const $pos = doc.resolve(pos);
    const after = $pos.nodeAfter;
    if (!after?.isText || !after.text) return null;
    return after.text[0] || null;
};

/**
 * Find the whitespace-delimited word range for a drop position.
 * Uses doc-position scanning, so inline atom nodes do not corrupt offsets.
 */
export function findDropWordRange(state, pos) {
    if (!state?.doc || typeof pos !== 'number') return null;

    const safePos = Math.max(0, Math.min(pos, state.doc.content.size));
    const $pos = state.doc.resolve(safePos);
    if (!$pos.parent?.isTextblock) return null;

    const parentStart = $pos.start($pos.depth);
    const parentEnd = $pos.end($pos.depth);

    const hasWordOnLeft = isWordCharacter(getTextCharBefore(state.doc, safePos));
    const hasWordOnRight = isWordCharacter(getTextCharAfter(state.doc, safePos));
    if (!hasWordOnLeft && !hasWordOnRight) return null;

    let from = safePos;
    while (from > parentStart && isWordCharacter(getTextCharBefore(state.doc, from))) {
        from -= 1;
    }

    let to = safePos;
    while (to < parentEnd && isWordCharacter(getTextCharAfter(state.doc, to))) {
        to += 1;
    }

    if (from === to) return null;
    return {from, to};
}

/**
 * Determine whether the drop is over an existing reference mark.
 * Uses ProseMirror coordinates -> doc position mapping (best practice).
 */
const referenceUidAtCoords = (view, x, y) => {
    const posResult = view.posAtCoords({left: x, top: y});
    if (DEBUG_SNIPPET_DROP) {
        console.log('[snippetDrop] posAtCoords', {x, y, posResult});
    }
    if (!posResult || typeof posResult.pos !== 'number') return null;

    const uid = referenceUidFromDocPos(view.state, posResult.pos);
    if (DEBUG_SNIPPET_DROP) {
        console.log('[snippetDrop] referenceUidFromDocPos', {pos: posResult.pos, uid});
    }
    return uid;
};

/**
 * Create snippet drop plugin
 * @param {Object} options - Plugin options
 * @param {string} options.reportId - The report ID for adding references
 * @param {Function} options.onReferenceAdded - Callback when reference is added (uid, snippet)
 * @param {Function} options.addToSourcePackage - Function to add snippet to source package
 */
export function snippetDropPlugin(options = {}) {
    const {reportId, onReferenceAdded, addToSourcePackage} = options;

    return new Plugin({
        key: snippetDropPluginKey,

        props: {
            handleDrop(view, event, slice, moved) {
                const snippets = readSnippetsFromDrop(event?.dataTransfer);
                if (!snippets.length) return false;

                try {
                    const citableSnippets = snippets.filter((snippet) => !isProductReportSource({
                        docSourceType: snippet?.docSourceType,
                        reportId: snippet?.reportId,
                        localId: snippet?.localId,
                    }));
                    if (!citableSnippets.length) {
                        return false;
                    }

                    const [firstSnippet, ...remainingSnippets] = citableSnippets;

                    if (DEBUG_SNIPPET_DROP) {
                        console.log('[snippetDrop] handleDrop', {
                            reportId,
                            clientX: event.clientX,
                            clientY: event.clientY,
                            snippetsCount: snippets.length,
                            snippet: firstSnippet,
                            hasSlice: !!slice,
                            moved,
                        });
                    }

                    // Check if dropped over existing reference mark (doc-based hit-test)
                    const referenceUid = referenceUidAtCoords(view, event.clientX, event.clientY);

                    if (referenceUid) {
                        if (DEBUG_SNIPPET_DROP) {
                            console.log('[snippetDrop] route=existingReference', {referenceUid});
                        }
                        // Add all dropped snippets to existing reference.
                        [firstSnippet, ...remainingSnippets].forEach((snippet) => {
                            handleDropOnExistingReference(view, referenceUid, snippet, reportId, onReferenceAdded, addToSourcePackage);
                        });
                        return true;

                    }

                    if (DEBUG_SNIPPET_DROP) {
                        console.log('[snippetDrop] route=newReferenceOnText');
                    }
                    // Otherwise, create a new reference mark and attach all dropped snippets to it.
                    const dropResult = handleDropOnText(view, event, firstSnippet, reportId, onReferenceAdded, addToSourcePackage);
                    if (!dropResult?.handled) {
                        return false;
                    }

                    if (dropResult.referenceUid && remainingSnippets.length) {
                        remainingSnippets.forEach((snippet) => {
                            handleDropOnExistingReference(
                                view,
                                dropResult.referenceUid,
                                snippet,
                                reportId,
                                onReferenceAdded,
                                addToSourcePackage
                            );
                        });
                    }
                    return true;

                } catch (e) {
                    console.error('Failed to handle snippet drop:', e);
                    return false;
                }
            },
        },
    });
}

function readSnippetsFromDrop(dataTransfer) {
    if (!dataTransfer) {
        return [];
    }

    const multipleRaw = dataTransfer.getData(SNIPPETS_DRAG_MIME);
    if (multipleRaw) {
        try {
            const parsed = JSON.parse(multipleRaw);
            if (Array.isArray(parsed)) {
                return parsed.filter(Boolean);
            }
        } catch (_) {
            // ignore malformed multi-snippet payload and fall back to single snippet mime.
        }
    }

    const singleRaw = dataTransfer.getData(SNIPPET_DRAG_MIME);
    if (!singleRaw) {
        return [];
    }

    try {
        const parsed = JSON.parse(singleRaw);
        return parsed ? [parsed] : [];
    } catch (_) {
        return [];
    }
}

/**
 * Handle drop on existing reference mark
 */
async function handleDropOnExistingReference(view, referenceUid, snippet, reportId, onReferenceAdded, addToSourcePackage) {
    if (!onReferenceAdded) {
        console.warn('Cannot add reference: missing callback');
        return;
    }

    try {
        if (DEBUG_SNIPPET_DROP) {
            console.log('[snippetDrop] handleDropOnExistingReference:start', {referenceUid});
        }

        const localId = computeLocalIdFromSnippet(snippet);

        if (DEBUG_SNIPPET_DROP) {
            console.log('[snippetDrop] existingReference:computedLocalId', {localId});
        }

        if (!localId) {
            console.warn('Cannot add reference: missing localId');
            return;
        }

        const referenceData = {localId, ...snippet};

        // Write to Y.Map inside a transact so it groups atomically with any
        // ProseMirror-originated Yjs mutations for Y.UndoManager.
        const {ydoc, yReferenceMarks} = useYDocStore.getState();
        if (!ydoc || !yReferenceMarks) {
            console.warn('[snippetDrop] Y.Doc not ready, cannot add reference');
            return;
        }
        ydoc.transact(() => {
            addReference(ydoc, yReferenceMarks, referenceUid, referenceData);
        }, null);

        if (DEBUG_SNIPPET_DROP) {
            console.log('[snippetDrop] existingReference:yMapWriteDone');
        }

        if (addToSourcePackage) {
            addToSourcePackage(snippet);
        }
        if (onReferenceAdded) {
            onReferenceAdded(referenceUid, snippet);
        }

        // Resulting level is manualBaseline ? max(manualBaseline, citationsFloor) :
        // citationsFloor — see referenceClassificationFloor.js.
        syncBlockClassificationFloorForReference(view, referenceUid, reportId);
    } catch (err) {
        console.error('Failed to add reference to existing mark:', err);
    }
}

/**
 * Handle drop on plain text - create new reference mark
 */
function handleDropOnText(view, event, snippet, reportId, onReferenceAdded, addToSourcePackage) {
    const posResult = view.posAtCoords({left: event.clientX, top: event.clientY});
    if (!posResult || typeof posResult.pos !== 'number') {
        return {handled: false, referenceUid: null};
    }

    const pos = posResult.pos;
    const {state, dispatch} = view;

    // If the user had a non-collapsed text selection and the drop landed inside
    // (or adjacent to) that selection, honour the full selection as the range
    // instead of shrinking it to the single word under the cursor.
    const existingSel = state.selection;
    const selIsNonCollapsed = existingSel.from < existingSel.to;
    const dropIsWithinSelection =
        selIsNonCollapsed &&
        pos >= existingSel.from - 1 &&
        pos <= existingSel.to + 1;

    let range;
    if (dropIsWithinSelection) {
        range = {from: existingSel.from, to: existingSel.to};
    } else {
        range = findDropWordRange(state, pos);
    }
    if (!range) return {handled: false, referenceUid: null};

    const {from, to} = range;

    const {ydoc, yReferenceMarks} = useYDocStore.getState();

    // Wrap mark creation + Y.Map write in a single ydoc.transact() so
    // Y.UndoManager captures everything as one atomic undo item.
    // When ydoc is not available (non-collab), fall through to plain dispatch.
    const doMarkCreation = () => {
        // Create a text selection at that range
        const trSel = state.tr.setSelection(TextSelection.create(state.doc, from, to));
        dispatch(trSel);

        // Apply reference mark via addMetaMark (synchronous ProseMirror dispatch)
        const {from: selFrom, to: selTo} = view.state.selection;
        if (selFrom === selTo) return null;
        const uid = addMetaMark(view, 'reference');
        if (!uid) return null;

        // Initialize mark in Y.Map
        if (ydoc && yReferenceMarks) {
            initializeReferenceMark(ydoc, yReferenceMarks, uid);
        }

        return uid;
    };

    let uid;
    if (ydoc) {
        ydoc.transact(() => {
            uid = doMarkCreation();
        }, null);
    } else {
        uid = doMarkCreation();
    }

    if (!uid) return {handled: false, referenceUid: null};

    view.focus();
    const newState = view.state;
    const trFocus = newState.tr.setSelection(TextSelection.create(newState.doc, to));
    view.dispatch(trFocus);

    if (reportId && onReferenceAdded) {
        const localId = computeLocalIdFromSnippet(snippet);

        if (!localId) {
            console.warn('Cannot add reference: missing localId');
            return {handled: false, referenceUid: uid};
        }

        try {
            const referenceData = {localId, ...snippet};

            if (!ydoc || !yReferenceMarks) {
                console.warn('[snippetDrop] Y.Doc not ready, cannot add reference');
                return {handled: false, referenceUid: uid};
            }
            // Write reference data inside a transact so it shares the same
            // Y.UndoManager capture window as the mark creation above.
            ydoc.transact(() => {
                addReference(ydoc, yReferenceMarks, uid, referenceData);
            }, null);

            if (addToSourcePackage) {
                addToSourcePackage(snippet);
            }
            onReferenceAdded(uid, snippet);

            // Resulting level is manualBaseline ? max(manualBaseline, citationsFloor) :
            // citationsFloor — see referenceClassificationFloor.js.
            syncBlockClassificationFloorForReference(view, uid, reportId);
        } catch (err) {
            console.error('Failed to add reference after mark creation:', err);
        }
    }

    return {handled: true, referenceUid: uid};
}

/**
 * Get the plugin state
 */
export function getSnippetDropPluginState(state) {
    return snippetDropPluginKey.getState(state);
}
